import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import API from "../../API";
import Comptinformations from "../../assets/Forms/Comptinformations/Comptinformations";
import Profileinformations from "../../assets/Forms/Profileinformations/Profileinformations";
import Reservationdemandes from "../../assets/Forms/Reservationdemandes/Reservationdemandes";
import Support from "../../assets/Forms/Support/Support";
import { coverImage, profileImage } from "../../assets/images/images";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import Topbar from "../../components/Topbar/Topbar";
import "./profile.css";
import { resolveImageUrl } from "../../constants";
import MyTickets from "../../assets/Forms/MyTickets/MyTickets";
import { useTranslation } from 'react-i18next';
import {
  FaUser, FaLock, FaCalendarAlt, FaHeadset, FaTicketAlt,
  FaCamera, FaEnvelope, FaChevronRight, FaCheckCircle, FaArrowLeft
} from 'react-icons/fa';

/* Subscribes to a media-query so we can render different markup per
   viewport instead of toggling visibility of two parallel trees. */
const useIsMobile = (breakpoint = 480) => {
  const get = () =>
    typeof window !== "undefined" && window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
  const [isMobile, setIsMobile] = useState(get);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener("change", handler) : mq.addListener(handler);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", handler) : mq.removeListener(handler);
    };
  }, [breakpoint]);
  return isMobile;
};

export default function Profile() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = parseInt(searchParams.get("tab")) || 0;
  const [view, setView] = useState(initialTab);
  const [userData, setUserData] = useState(null);
  const profileimgRef = useRef(null);
  const isMobile = useIsMobile(480);

  /* On mobile, "menu" is the index view (no tab opened). The URL drives
     the open tab — if `tab` param is absent we show the menu, otherwise
     we show that tab's content. */
  const tabFromUrl = searchParams.has("tab")
    ? parseInt(searchParams.get("tab"))
    : null;

  // Sync view with URL tab param when it changes
  useEffect(() => {
    const tabParam = parseInt(searchParams.get("tab")) || 0;
    setView(tabParam);
  }, [searchParams]);

  // Update URL when view changes via sidebar click
  const handleViewChange = (newView) => {
    setView(newView);
    setSearchParams({ tab: newView.toString() });
  };

  // Mobile back-to-menu: clear the tab param entirely.
  const handleMobileBack = () => {
    setSearchParams({});
  };

  const handleProfileImgClick = () => {
    profileimgRef.current.click();
  };

  const handleProfileImgChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("imageurl", file);

    const token = localStorage.getItem("protoken"); // Retrieve the token
    const userId = localStorage.getItem("userId"); // Retrieve user ID

    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      };

      // Send the image to the server to update the profile image
      const response = await API.patch(`/users/${userId}`, formData, config);
      
      // Update the user data with the new image URL
      setUserData((prevData) => ({
        ...prevData,
        image: response.data.imageurl,
      }));
    } catch (error) {
      console.error("Error updating profile image:", error);
    }
  };

  const fetchUserData = async () => {
    const token = localStorage.getItem("protoken"); // Retrieve the token
    const userId = localStorage.getItem("userId"); // Retrieve user ID from local storage
    if (!token || !userId) {
      console.error("No token or user ID found");
      return;
    }

    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await API.get(`/users/${userId}`, config); // Use the retrieved user ID
      setUserData(response.data); // Store user data in state
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  useEffect(() => {
    fetchUserData(); // Fetch user data when the component mounts
  }, []);

  const menuItems = [
    { id: 0, Icon: FaUser,         key: "personal_info" },
    { id: 1, Icon: FaLock,         key: "account_info" },
    { id: 2, Icon: FaCalendarAlt,  key: "reservations_requests" },
    { id: 3, Icon: FaTicketAlt,    key: "my_tickets" },
    { id: 4, Icon: FaHeadset,      key: "help_support" },
  ];

  const fullName = userData ? `${userData.firstname} ${userData.lastname}` : t("profile.name");
  const joinYear = userData?.createdAt ? userData.createdAt.substring(0, 4) : "—";
  const activeTitle = menuItems.find((m) => m.id === view)?.key;

  /* Renders the active form for whichever tab is open. */
  const renderTabContent = () => (
    <>
      {view === 0 ? <Profileinformations userData={userData} /> : ""}
      {view === 1 ? <Comptinformations userData={userData} /> : ""}
      {view === 2 ? <Reservationdemandes userData={userData} /> : ""}
      {view === 3 ? <MyTickets /> : ""}
      {view === 4 ? <Support onTicketCreated={() => handleViewChange(3)} /> : ""}
    </>
  );

  /* ═══ MOBILE LAYOUT — settings-app style ═══
     Two views: the "menu" (avatar header + vertical settings list) and
     the "tab" (top bar with back button + form). Selecting a tab pushes
     to the tab view; back returns to the menu. */
  if (isMobile) {
    const inTab = tabFromUrl !== null;

    return (
      <div className="Page_profile is-mobile">
        <input
          type="file"
          style={{ display: "none" }}
          ref={profileimgRef}
          onChange={handleProfileImgChange}
        />
        <Topbar />
        <Navbar />

        {!inTab ? (
          /* ── Index / menu view ── */
          <main className="ppm-menu-page">
            <header className="ppm-identity">
              <div className="ppm-identity__avatar">
                <img
                  src={userData && userData.image ? resolveImageUrl(userData.image) : profileImage}
                  alt={t("profile.profile_image_alt")}
                />
                <button
                  type="button"
                  className="ppm-identity__edit"
                  onClick={handleProfileImgClick}
                  aria-label={t("profile.edit_icon_alt")}
                >
                  <FaCamera />
                </button>
              </div>
              <h1 className="ppm-identity__name">{fullName}</h1>
              {userData?.email && (
                <p className="ppm-identity__email">{userData.email}</p>
              )}
              <div className="ppm-identity__chips">
                <span className="ppm-chip">
                  <FaCalendarAlt /> {t("profile.member_since")} {joinYear}
                </span>
                <span className={`ppm-chip ${userData ? "is-success" : ""}`}>
                  <FaCheckCircle /> {userData ? t("profile.active") : t("profile.not_active")}
                </span>
              </div>
            </header>

            <nav className="ppm-list" aria-label={t("profile.personal_info")}>
              {menuItems.map((item) => {
                const Icon = item.Icon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className="ppm-row"
                    onClick={() => handleViewChange(item.id)}
                  >
                    <span className="ppm-row__icon"><Icon /></span>
                    <span className="ppm-row__label">{t(`profile.${item.key}`)}</span>
                    <FaChevronRight className="ppm-row__chev" />
                  </button>
                );
              })}
            </nav>
          </main>
        ) : (
          /* ── Tab view ── */
          <main className="ppm-tab-page">
            <header className="ppm-tab__bar">
              <button
                type="button"
                className="ppm-tab__back"
                onClick={handleMobileBack}
                aria-label={t("profile.back") || "Back"}
              >
                <FaArrowLeft />
              </button>
              <h1 className="ppm-tab__title">
                {activeTitle ? t(`profile.${activeTitle}`) : ""}
              </h1>
            </header>
            <div className="ppm-tab__body">{renderTabContent()}</div>
          </main>
        )}

        <Footer />
      </div>
    );
  }

  /* ═══ DESKTOP LAYOUT — original card-in-card design ═══ */
  return (
    <div className="Page_profile">
      <input
        type="file"
        style={{ display: "none" }}
        ref={profileimgRef}
        onChange={handleProfileImgChange}
      />
      <Topbar />
      <Navbar />

      {/* ═══ Hero ═══ */}
      <section className="pp-hero">
        <div
          className="pp-hero__cover"
          style={{ backgroundImage: `url(${coverImage})` }}
        >
          <div className="pp-hero__scrim" />
        </div>

        <div className="pp-hero__card">
          <div className="pp-avatar">
            <img
              src={userData && userData.image ? resolveImageUrl(userData.image) : profileImage}
              alt={t("profile.profile_image_alt")}
            />
            <button
              type="button"
              className="pp-avatar__edit"
              onClick={handleProfileImgClick}
              aria-label={t("profile.edit_icon_alt")}
            >
              <FaCamera />
            </button>
          </div>

          <div className="pp-hero__info">
            <h1 className="pp-hero__name">{fullName}</h1>
            {userData?.email && (
              <p className="pp-hero__email">
                <FaEnvelope /> <span>{userData.email}</span>
              </p>
            )}
            <div className="pp-hero__chips">
              <span className="pp-chip pp-chip--muted">
                <FaCalendarAlt /> {t("profile.member_since")} {joinYear}
              </span>
              <span className={`pp-chip ${userData ? "pp-chip--success" : "pp-chip--muted"}`}>
                <FaCheckCircle /> {userData ? t("profile.active") : t("profile.not_active")}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ Layout ═══ */}
      <div className="pp-layout">
        {/* Sidebar */}
        <aside className="pp-sidebar">
          <div className="pp-sidebar__label">{t("profile.personal_info")}</div>
          <nav className="pp-menu">
            {menuItems.map((item) => {
              const Icon = item.Icon;
              const isActive = view === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  className={`pp-menu__item ${isActive ? "is-active" : ""}`}
                  onClick={() => handleViewChange(item.id)}
                  aria-current={isActive ? "page" : undefined}
                >
                  <span className="pp-menu__icon"><Icon /></span>
                  <span className="pp-menu__text">{t(`profile.${item.key}`)}</span>
                  <FaChevronRight className="pp-menu__chev" />
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Content */}
        <main className="pp-content">
          <header className="pp-content__head">
            <h2 className="pp-content__title">
              {activeTitle ? t(`profile.${activeTitle}`) : ""}
            </h2>
          </header>

          <div className="pp-content__body">{renderTabContent()}</div>
        </main>
      </div>

      <Footer />
    </div>
  );
}